import { zodResolver } from '@hookform/resolvers/zod';
import type { ComponentProps } from 'react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { getLandingPrivacyUrl } from '@/utils/landingUrl';

export function LoginForm({
  className,
  ...props
}: ComponentProps<'div'>): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const privacyUrl = getLandingPrivacyUrl(i18n.language ?? i18n.resolvedLanguage ?? 'en');
  const { login } = useAuth();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const loginSchema = useMemo(
    () =>
      z.object({
        email: z.string().email(t('validation.emailInvalid')),
        password: z.string().min(6, t('validation.passwordMin')),
      }),
    [t],
  );
  type LoginFormValues = z.infer<typeof loginSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (values: LoginFormValues): Promise<void> => {
    setErrorMessage(null);
    try {
      await login(values);
      // Navigate to root, RootRedirect will handle the correct destination
      navigate('/', { replace: true });
    } catch (error) {
      setErrorMessage(t('auth.login.error'));
    }
  };

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card className="overflow-hidden">
        <CardContent className="grid p-0 md:grid-cols-2">
          <form className="p-6 md:p-8" onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-6">
              <div className="flex items-center justify-between gap-2 flex-col">
                <h1 className="text-2xl font-bold">
                  {t('auth.login.welcome')}
                </h1>
                <p className="text-balance text-muted-foreground">
                  {t('auth.login.subtitle')}
                </p>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="email">{t('auth.login.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="m@example.com"
                  {...register('email')}
                />
                {errors.email ? (
                  <p className="text-sm text-destructive">
                    {errors.email.message}
                  </p>
                ) : null}
              </div>
              <div className="grid gap-2">
                <div className="flex items-center">
                  <Label htmlFor="password">{t('auth.login.password')}</Label>
                  <Link
                    to="/forgot-password"
                    className="ml-auto text-sm underline-offset-2 hover:underline"
                  >
                    {t('auth.login.forgotPassword')}
                  </Link>
                </div>
                <Input id="password" type="password" {...register('password')} />
                {errors.password ? (
                  <p className="text-sm text-destructive">
                    {errors.password.message}
                  </p>
                ) : null}
              </div>
              {errorMessage ? (
                <p className="text-sm text-destructive">{errorMessage}</p>
              ) : null}
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting
                  ? t('auth.login.submitting')
                  : t('auth.login.submit')}
              </Button>
            </div>
          </form>
          <div className="hidden items-center justify-center bg-muted p-8 md:flex">
            <img
              src="/brand/trushelf-horizontal.svg"
              alt="TruShelf"
              className="w-2/3 max-w-xs"
            />
          </div>
        </CardContent>
      </Card>
      <div className="text-balance text-center text-xs text-white/90 [&_a]:underline [&_a]:underline-offset-4 [&_a]:text-white [&_a]:hover:text-secondary">
        {t('auth.login.termsIntro')}{' '}
        <a href={privacyUrl} target="_blank" rel="noopener noreferrer">
          {t('auth.login.privacy')}
        </a>
        .
      </div>
    </div>
  );
}
